import Button from '@/atoms/Button'
import Img from '@/atoms/Img'
import { ContactCTAGeneral } from '@/content/types'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import NextLink from 'next/link'
import { attributes } from '../content/contact_cta.md'
import { useLocale } from '@/hooks/useLocale'
import { VariantProps, cva } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'

const contactCTAClassVariance = cva(
  'flex gap-8 md:gap-16 flex-col md:flex-row items-center mx-auto py-32 text-black justify-center',
  {
    variants: {
      variant: {
        light: 'bg-gray-700',
        bright: 'bg-white',
        blue: 'bg-blue text-white',
      },
    },
    defaultVariants: {
      variant: 'bright',
    },
  },
)

type ContactCTAType = VariantProps<typeof contactCTAClassVariance>

export interface ContactCTAProps extends ContactCTAType {
  className?: string
}

const ContactCTA = ({ variant = 'bright', className }: ContactCTAProps) => {
  const { translations } = useLocale<ContactCTAGeneral>(attributes)
  const cls = twMerge(contactCTAClassVariance({ variant }), className)
  const isBlue = variant === 'blue'

  return (
    <div className={cls}>
      <Img
        src={translations.contact_cta.image_url}
        className={twMerge(
          'object-cover rounded-full shrink-0 w-40 h-40 md:w-72 md:h-72',
        )}
      />
      <div className="flex flex-col xl:flex-row h-fit items-center md:items-start xl:items-end gap-8 xl:gap-16">
        <div className="grow text-center md:text-left max-w-[460px]">
          <h2 className="font-medium text-2xl md:text-3xl mb-8 leading-relaxed md:leading-loose">
            {translations.contact_cta.main_title}
          </h2>
          {translations.contact_cta.check_items.map((item) => (
            <span className="block text-sm mt-2" key={item}>
              <FontAwesomeIcon
                icon={faCheckCircle}
                className={twMerge(
                  'mr-3',
                  isBlue ? 'text-white' : 'text-green',
                )}
              />
              {item}
            </span>
          ))}
        </div>
        <NextLink href="/contact" className="shrink-0 text-sm">
          <Button variant={isBlue ? 'black' : 'primary'}>
            {translations.contact_cta.button_text}
          </Button>
        </NextLink>
      </div>
    </div>
  )
}

export default ContactCTA
